import { Add, Document, DocumentView, ViewOff } from '@carbon/react/icons';
import {
  Button,
  Column,
  FlexGrid as Grid,
  Link,
  Row,
  Tile,
} from '@carbon/react';
import classNames from 'classnames';
import { Date } from 'components/date';
import { Atom, atom, useAtomValue } from 'jotai';
import { compact } from 'lodash-es';
import plur from 'plur';
import { DocumentResponse, TextBasedEntityResponse } from 'types/api-responses';
import { LinkWithHover } from 'components/hover-card-resource';
import style from './document-card.module.scss';

const NULL_DISPLAY_VALUE = '--';
interface Props {
  document: DocumentResponse;
  onAddDocument?: (doc: DocumentResponse) => void;
  usedDocumentsAtom: Atom<number[]>;
  onViewDocument: (documentId: number | undefined) => void;
  selectedDocumentId?: number;
}

const usedDocumentReferencesAtom = atom<number[]>([]);

const DocumentCard = ({
  document,
  onAddDocument,
  usedDocumentsAtom,
  onViewDocument,
  selectedDocumentId,
}: Props) => {
  const isSelected = selectedDocumentId === document.id;

  const secondaryHeaderData = compact([
    document.county &&
      `${document.county.name}, ${document.county.state.abbreviation}`,
    document.bookType,
    document.documentType,
  ]).join(', ');

  const usedDocumentReferences = useAtomValue(usedDocumentsAtom);

  const handleViewDocument = () => {
    onViewDocument(isSelected ? undefined : document.id);
  };

  return (
    <Tile className={style.documentCard}>
      <div className={classNames(style.background, style.document)}>
        <Document
          size={32}
          className={classNames(style.icon, style.document)}
        />
      </div>
      <div className={style.content}>
        <header className={style.header}>
          <h3 className={style.secondaryHeader}>{secondaryHeaderData}</h3>
          <h2 className={style.primaryTitle}>
            <LinkWithHover kind="document" href={document.href}>
              <Link
                className={style.primaryHeader}
                href={document.href}
                onClick={() => {}}
                target="_blank"
              >
                {document.documentTitle || 'Unknown'}
              </Link>
            </LinkWithHover>
          </h2>
        </header>
        <Grid className="bx--no-gutter" fullWidth>
          <Row className={style.row}>
            <Column>
              <section className={style.truncate}>
                <h3 className={style.title}>Created At:</h3>
                {document.createdAt ? (
                  <Date date={document.createdAt} />
                ) : (
                  NULL_DISPLAY_VALUE
                )}
              </section>
            </Column>
            <Column>
              <section className={style.truncate}>
                <h3 className={style.title}>
                  {plur('Grantor', document.grantors.length)}:
                </h3>
                ({document.grantors.length})
                {document.grantors
                  .map((grantor: TextBasedEntityResponse) => grantor.name)
                  .join(', ')}
              </section>
            </Column>
            <Column>
              <section className={style.truncate}>
                <h3 className={style.title}>
                  {plur('Grantee', document.grantees.length)}:
                </h3>
                ({document.grantees.length})
                {document.grantees
                  .map((grantee: TextBasedEntityResponse) => grantee.name)
                  .join(', ')}
              </section>
            </Column>
          </Row>
          <Row className={style.row}>
            <Column>
              <section className={style.truncate}>
                <h3 className={style.title}>Updated At:</h3>
                {document.updatedAt ? (
                  <Date date={document.updatedAt} />
                ) : (
                  NULL_DISPLAY_VALUE
                )}
              </section>
            </Column>
            <Column>
              <section>
                <h3 className={style.title}>Effective Date: </h3>
                {document.effectiveDate ? (
                  <Date date={document.effectiveDate} />
                ) : (
                  NULL_DISPLAY_VALUE
                )}
              </section>
            </Column>
            <Column>
              <section>
                <h3 className={style.title}>Recorded Date: </h3>
                {document.recordedDate ? (
                  <Date date={document.recordedDate} />
                ) : (
                  NULL_DISPLAY_VALUE
                )}
              </section>
            </Column>
          </Row>
        </Grid>
      </div>
      {onAddDocument && (
        <div>
          <Button
            iconDescription="Add Document Reference"
            renderIcon={Add}
            size="sm"
            disabled={usedDocumentReferences.includes(document.id)}
            onClick={() => onAddDocument(document)}
          >
            Add
          </Button>
        </div>
      )}
      <div className={style.actions}>
        <Button
          kind="secondary"
          hasIconOnly
          iconDescription={isSelected ? 'Close Preview' : 'Preview Document'}
          size="md"
          onClick={handleViewDocument}
          renderIcon={isSelected ? ViewOff : DocumentView}
          tooltipAlignment="end"
          tooltipPosition="bottom"
          className={style.viewButton}
        />
      </div>
    </Tile>
  );
};

export { usedDocumentReferencesAtom, DocumentCard };
